import { AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import UnreachableNotice from '@/shared/components/UnreachableNotice';
import { ageJoignabilite, estJoignable, etatJoignabilite } from '@/shared/utils/liveness';

// Ré-exporté ici pour que l'écran de pilotage n'ait qu'un point d'entrée : le composant
// vit dans `shared` parce que le compte rendu d'après-envoi s'en sert aussi.
export { UnreachableNotice };

/**
 * Confirmation d'une action UNITAIRE de pilotage.
 *
 * Rappelle systématiquement que le geste sera tracé : c'est ce qui rend le pilotage
 * acceptable sur l'installation d'un client — rien ne part sans nom ni horodatage.
 *
 * Le pilotage GROUPÉ n'est plus traité ici : il passe par `shared/components/BulkEditDialog`,
 * commun à toutes les zones admin.
 */
export function ConfirmSingleDialog({ pending, actionDef, onCancel, onConfirm }) {
  const open = !!pending;
  const warning = pending?.warning || actionDef?.warning;
  const simulated = actionDef?.integration === 'simulated';
  // Prévenir AVANT le clic, sur les seules machines dont on mesure la joignabilité : une
  // batterie. Rien ne mesure celle d'une PAC pilotée par API constructeur, et un
  // avertissement inventé userait le seul qui compte.
  const etatBatterie = pending?.asset?.type === 'Batterie'
    ? etatJoignabilite(pending.asset) : null;
  const injoignable = !!etatBatterie && !estJoignable(etatBatterie);

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="confirm-single-dialog">
        <DialogHeader><DialogTitle>Confirmer l'action</DialogTitle></DialogHeader>
        {pending && (
          <div className="space-y-3 text-[13px]">
            <div className="p-3 rounded-md bg-neutral-50 border border-neutral-100">
              <p className="text-[11px] text-neutral-400">Actif ciblé</p>
              <p className="font-medium text-neutral-900">
                {pending.asset.client_nom} — {pending.asset.type} {pending.asset.marque}
              </p>
            </div>
            <p className="text-neutral-700">
              Action : <span className="font-semibold text-neutral-900">
                {actionDef?.label || pending.action}
              </span>
              {pending.value != null && (
                <span className="ml-1 text-cyan-700 font-semibold">
                  → {pending.value} {actionDef?.value_unit || ''}
                </span>
              )}
            </p>

            {simulated && <SimulatedNotice />}

            {injoignable && (
              <UnreachableNotice
                etat={etatBatterie}
                ageS={ageJoignabilite(pending.asset)}
                avant
              />
            )}

            {warning && (
              <div
                className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-100 text-[12px] text-amber-800"
                data-testid="warning-message"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                <span>{warning}</span>
              </div>
            )}

            <p className="text-[11px] text-neutral-400 pt-2 border-t border-neutral-100">
              Cette action sera tracée dans le journal avec votre nom et l'horodatage.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="cancel-btn">Annuler</Button>
          <Button onClick={onConfirm} data-testid="confirm-btn">Confirmer et exécuter</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Dit franchement que le geste ne touchera pas le matériel. */
export function SimulatedNotice() {
  return (
    <div
      className="flex items-start gap-2 p-3 rounded-md bg-neutral-100 border border-neutral-200 text-[12px] text-neutral-700"
      data-testid="simulated-notice"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-neutral-500" />
      <span>
        Aucune intégration n'est branchée pour cette action : elle sera enregistrée dans le
        journal mais <strong>n'aura aucun effet sur l'équipement</strong>.
      </span>
    </div>
  );
}
